/* eslint-disable no-restricted-globals */
import React, { useEffect, useState, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { Selection, Stack, PrimaryButton } from '@fluentui/react';
import moment from 'moment';
import { toast } from 'react-toastify';
import {
  overviewClassNames as classNames,
  convertEmployeesToComboBoxOptions,
  DEFAULT_ERROR_MESSAGE,
  DEFAULT_LOADING_MESSAGE,
  DEFAULT_TOAST_DURATION,
  DEFAULT_TOAST_POSITION,
  DELETE_CONSTRUCTION_SITE,
  GET_EMPLOYEES,
  IConstructionSite,
  IEmployee,
} from '../../../utils';

import {
  IConstructionSiteCreateUpdate,
} from '../../../utils/ConstructionSite';
import {
  Filter,
  FilterPanel,
  FilterState,
  initFilterPanelState,
} from '../../../components/parts/FilterPanel';
import {
  GET_CONSTRUCTION_SITE_STATUSES,
  convertConstructionSiteStatusesToComboBoxOptions,
} from '../../../utils/ConstructionSiteStatus';
import { useAppDispatch } from '../../../redux/hooks';
import { sendNotification } from '../../../redux/notification/notificationSlice';
import { GET_TAX_CODES } from '../../../utils/TaxCode';
import { toastError, toastSuccess } from '../../../utils/toast';

import WetBreyneConstructionSitesDetailsList from './WetBreyneConstructionSitesDetailsList';
import WetBreyneConstructionSiteDetail from './WetBreyneConstructionSiteDetail';
import WetBreyneConstructionSitesCommandBar from './WetBreyneConstructionSitesCommandBar';

interface Props {
  constructionSites: IConstructionSite[];
  loading?: boolean;
  //
  setSorting: (isSortedAsc: boolean, sortedField: string) => void;
  isSortedAsc?: boolean;
  sortedField?: string;
  //
  showLoadMore?: boolean;
  loadMore?: boolean;
  loadMoreCallback?: () => void;
  lazyLoading?: boolean;
  //
  downloadFile?: () => void;
  //
  listFilters: any;
  setConstructionSiteFilter: React.Dispatch<
    React.SetStateAction<FilterState | undefined>
  >;
  //
  isFilterPanelOpen: boolean;
  setIsFilterPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  //
  refetch: any;
}

const WetBreyneOverview = ({
  constructionSites,
  loading,
  //
  setSorting = (isSortedAsc: boolean, sortedField: string) => {},
  isSortedAsc = false,
  sortedField = '',
  //
  showLoadMore,
  loadMore,
  loadMoreCallback = () => ({}),
  lazyLoading,
  //
  downloadFile = () => {},
  //
  listFilters,
  setConstructionSiteFilter,
  //
  isFilterPanelOpen,
  setIsFilterPanelOpen,
  //
  refetch,
}: Props) => {
  // States
  const [announcedMessage] = useState('');
  const [isDataLoaded, setIsDataLoaded] = useState(!loading);
  const [showWarning, setShowWarning] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [taxCodes, setTaxCodes] = useState([]);
  const [statuses, setStatuses] = useState([]);

  const [openPanel, setOpenPanel] = useState(false);
  const [openPanelFinancial, setOpenPanelFinancial] = useState(false);
  const [openPanelFinancialPdf, setOpenPanelFinancialPdf] = useState(false);
  const [constructionSite, setConstructionSite] = useState<
    IConstructionSiteCreateUpdate | undefined
  >(undefined);

  const { id: constructionSiteId } = useParams();

  // Queries
  // Geen refetchQueries zonder variabelen: die haalde álle werven op voor een lijst die nergens
  // getoond wordt. Na verwijderen volgt refetch() van de zichtbare lijst.
  const [deleteConstructionSite] = useMutation(DELETE_CONSTRUCTION_SITE);
  const dispatch = useAppDispatch();
  // Selection
  /* const [constructionSite, setconstructionSite] = useState<
    IConstructionSite | undefined
  >(); */

  const getconstructionSite = () => {
    const currentSelection: any = selection.getSelection();

    if (currentSelection.length > 0) {
      setConstructionSite(currentSelection[0]);
    } else {
      setConstructionSite(undefined);
    }
  };

  const selection = new Selection({
    onSelectionChanged: getconstructionSite,
  });

  // Paneel geopend via "Nieuw"? Dan bij het sluiten de lijst verversen, zodat het nieuwe record
  // erin staat (de lijst komt nu uit de cache). Een gewijzigd record wordt al bijgewerkt via het
  // mutatie-antwoord, zo blijft een met "Toon meer" uitgebreide lijst staan.
  const openedAsNew = useRef(false);
  useEffect(() => {
    if (!openPanel && openedAsNew.current) {
      openedAsNew.current = false;
      refetch();
    }
  }, [openPanel]);

  const openDetail = (newSite?: boolean) => {
    openedAsNew.current = !!newSite;
    if (newSite) {
      setConstructionSite({});
    }
    setOpenPanel(true);
  };

  // Const
  const navigate = useNavigate();

  const gotoExecutionList = (id: number) => {
    window.open(`/construction-sites/${id}/implementation-list`, '_blank');
    // navigate(`/construction-sites/${id}/implementation-list`);
  };
  const gotoWaterPoints = (id: number) => {
    window.open(`/construction-sites/${id}/water-points`, '_blank');
    // navigate(`/construction-sites/${id}/implementation-list`);
  };
  const gotoConstructionSheet = (id: number) => {
    window.open(`/construction-sites/${id}/site-overview`, '_blank');
    // navigate(`/construction-sites/${id}/site-overview`);
  };
  const gotoAddPurchaseOrder = (id: number) => {
    window.open(
      `/purchase-orders?addNew=true&constructionSite=${id}`,
      '_blank',
    );
  };

  const removeConstructionSite = async () => {
    setIsDataLoaded(false);

    const sitesToBeDeleted = constructionSite;
    if (sitesToBeDeleted) {
      try {
        const result = await toast.promise(
          deleteConstructionSite({
            variables: {
              where: {
                id: sitesToBeDeleted.id,
              },
            },
          }),
          {
            pending: {
              position: DEFAULT_TOAST_POSITION,
              render() {
                return DEFAULT_LOADING_MESSAGE;
              },
            },
          },
          {
            autoClose: DEFAULT_TOAST_DURATION,
          },
        );

        // After successful deletion, you might want to update the state or refetch data
        // clearConstructionSites();
        // refetchConstructionSites();
        setIsDataLoaded(true);
        setShowWarning(false);
        toastSuccess('Construction site removed successfully!');
        await refetch();
      } catch (error: any) {
        setIsDataLoaded(true);
        // Optionally handle more specific error operations here
        toastError(error.message ? error.message : DEFAULT_ERROR_MESSAGE);
      }
    }
  };

  const loadExecutionList = () => {
    if (constructionSite && constructionSite.id) {
      gotoExecutionList(constructionSite.id);
    }
  };

  const loadWaterPoints = () => {
    if (constructionSite && constructionSite.id) {
      gotoWaterPoints(constructionSite.id);
    }
  };

  const loadConstructionSheet = () => {
    if (constructionSite && constructionSite.id) {
      gotoConstructionSheet(constructionSite.id);
    }
  };

  const loadNewPurchaseOrder = () => {
    if (constructionSite && constructionSite.id) {
      gotoAddPurchaseOrder(constructionSite.id);
    }
  };

  const dismissPanel = () => {
    setOpenPanel(false);
  };

  const dismissFinancialPanel = () => {
    setOpenPanelFinancial(false);
  };

  useEffect(() => {
    if (constructionSite && openPanel) {
      document.title = `3bouw | Werf - ${constructionSite.name}`;
    } else {
      document.title = '3bouw | Werven';
    }
  }, [constructionSite, openPanel]);

  useEffect(() => {
    if (constructionSiteId) {
      setOpenPanel(true);
    }
  }, []);

  useQuery(GET_EMPLOYEES, {
    fetchPolicy: 'no-cache',
    onCompleted: (x: any) => {
      setEmployees(x.findManyEmployees);
    },
  });

  useQuery(GET_TAX_CODES, {
    fetchPolicy: 'no-cache',
    onCompleted: (x: any) => {
      setTaxCodes(x.findManyTaxCodes);
    },
  });

  useQuery(GET_CONSTRUCTION_SITE_STATUSES, {
    fetchPolicy: 'no-cache',
    onCompleted: (x: any) => {
      setStatuses(x.findManyConstructionSiteStatuses);
    },
  });

  const filter_schema: Filter = {
    id: 'construction_site_filter_schema_rev2',
    permission: 'read:leads',
    schema: [
      {
        id: 'section_0',
        label: 'Algemeen',
        width: 48,
        fields: [
          {
            id: 'section_0_0',
            name: 'Adres',
            fieldName: 'address',
            data: 'string',
            type: 'text',
            callback: (fieldValue: string) => ({
              startsWith: fieldValue,
            }),
          },
          {
            id: 'section_0_1',
            name: 'Stad',
            fieldName: 'city',
            data: 'string',
            type: 'text',
            width: 69.5,
            callback: (fieldValue: string) => ({
              startsWith: fieldValue,
            }),
          },
          {
            id: 'section_0_2',
            name: 'Post code',
            fieldName: 'zip_code',
            data: 'number',
            type: 'number',
            width: 29.5,
            callback: (fieldValue: string) => parseInt(fieldValue, 10),
          },

          {
            id: 'section_0_4',
            name: 'Startdatum verkoop',
            fieldName: 'date_sold',
            data: 'date',
            type: 'date',
            width: 48.5,
            callback: (fieldValue: string) => ({
              gte: moment(new Date(fieldValue)).toISOString(),
            }),
          },
          {
            id: 'section_0_5',
            name: 'Einddatum verkoop',
            fieldName: 'date_sold',
            data: 'date',
            type: 'date',
            width: 48.5,
            callback: (fieldValue: string) => ({
              lte: moment(new Date(fieldValue)).toISOString(),
            }),
          },
          {
            id: 'section_0_6',
            name: 'Startdatum contract',
            fieldName: 'date_of_contract',
            data: 'date',
            type: 'date',
            width: 48.5,
            callback: (fieldValue: string) => ({
              gte: moment(new Date(fieldValue)).toISOString(),
            }),
          },
          {
            id: 'section_0_7',
            name: 'Einddatum contract',
            fieldName: 'date_of_contract',
            data: 'date',
            type: 'date',
            width: 48.5,
            callback: (fieldValue: string) => ({
              lte: moment(new Date(fieldValue)).toISOString(),
            }),
          },
          {
            id: 'section_0_8',
            name: 'Startdatum oplevering',
            fieldName: 'date_completed',
            data: 'date',
            type: 'date',
            width: 48.5,
            callback: (fieldValue: string) => ({
              gte: moment(new Date(fieldValue)).toISOString(),
            }),
          },
          {
            id: 'section_0_9',
            name: 'Einddatum oplevering',
            fieldName: 'date_completed',
            data: 'date',
            type: 'date',
            width: 48.5,
            callback: (fieldValue: string) => ({
              lte: moment(new Date(fieldValue)).toISOString(),
            }),
          },
        ],
      },
      {
        id: 'section_3',
        label: 'Wet Breyne',
        width: 48,
        fields: [
          {
            id: 'section_3_8',
            name: 'Startdatum  voorlopige levering',
            fieldName: 'breyne_date_provisional_delivery',
            data: 'date',
            type: 'date',
            width: 48.5,
            callback: (fieldValue: string) => ({
              gte: moment(new Date(fieldValue)).toISOString(),
            }),
          },
          {
            id: 'section_3_9',
            name: 'Einddatum voorlopige levering',
            fieldName: 'breyne_date_provisional_delivery',
            data: 'date',
            type: 'date',
            width: 48.5,
            callback: (fieldValue: string) => ({
              lte: moment(new Date(fieldValue)).toISOString(),
            }),
          },
          {
            id: 'section_3_10',
            name: 'Startdatum  definitieve levering',
            fieldName: 'breyne_date_final_delivery',
            data: 'date',
            type: 'date',
            width: 48.5,
            callback: (fieldValue: string) => ({
              gte: moment(new Date(fieldValue)).toISOString(),
            }),
          },
          {
            id: 'section_3_11',
            name: 'Einddatum definitieve levering',
            fieldName: 'breyne_date_final_delivery',
            data: 'date',
            type: 'date',
            width: 48.5,
            callback: (fieldValue: string) => ({
              lte: moment(new Date(fieldValue)).toISOString(),
            }),
          },
        ],
      },

    ],
  };

  const [filterState, setFilterState] = useState<FilterState>(
    initFilterPanelState(filter_schema),
  );

  return (
    <>
      {/* <ConstructionSitesCommandBar
        modifyFn={openDetail}
        modifyDisabled={!constructionSite}
        loadExecutionListFn={loadExecutionList}
        loadWaterPointsFn={loadWaterPoints}
        loadExecutionListDisabled={!constructionSite}
        loadConstructionSheetFn={loadConstructionSheet}
        loadConstructionSheetDisabled={!constructionSite}
        loadNewPurchaseOrderFn={loadNewPurchaseOrder}
        loadNewPurchaseOrderDisabled={!constructionSite}
        deleteFn={() => setShowWarning(true)}
        deleteDisabled={!constructionSite}
        downloadFile={downloadFile}
        openFilterPanel={setIsFilterPanelOpen}
        analyseFn={openFinancial}
        analysePdfFn={openFinancialPdf}
        constructionSites={constructionSites}
      /> */}

      <WetBreyneConstructionSitesCommandBar
        openFilterPanel={setIsFilterPanelOpen}
        constructionSites={constructionSites}
      />

      {announcedMessage && (
        <div className={classNames.selectionDetails}>{announcedMessage}</div>
      )}

      {isFilterPanelOpen && (
        <FilterPanel
          isOpen={isFilterPanelOpen}
          dismissPanel={() => {
            setIsFilterPanelOpen(false);
          }}
          filter={filter_schema}
          setFilter={setConstructionSiteFilter}
          filterState={filterState}
          setFilterState={setFilterState}
        />
      )}

      <WetBreyneConstructionSitesDetailsList
        items={constructionSites}
        selection={selection}
        // onItemInvoked={onItemInvoked}
        enableShimmer={!isDataLoaded}
        isSortedAsc={isSortedAsc}
        sortedField={sortedField}
        setSorting={setSorting}
        loadMore={loadMore}
        lazyLoading={lazyLoading}
        loadMoreCallback={loadMoreCallback}
        onItemInvoked={() => {
          openDetail();
        }}
        loading={loading}
      />

      {!loading && !showLoadMore && constructionSites.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          Geen resultaten
        </div>
      )}

      {showLoadMore && (
        <Stack
          style={{
            marginTop: '15px',
            marginLeft: 'auto',
            marginRight: 'auto',
            marginBottom: '15px',
            // display: items.length === costCenters.length ? 'block' : 'none',
          }}
          horizontal
          horizontalAlign='center'
        >
          <PrimaryButton
            text='Toon meer'
            onClick={() => {
              loadMoreCallback();
            }}
          />
        </Stack>
      )}

      {openPanel && (constructionSite || constructionSiteId) && (
        <WetBreyneConstructionSiteDetail
          isOpen={openPanel}
          dismissPanel={() => dismissPanel()}
          constructionSiteSource={constructionSite || {}}
          listFilters={listFilters}
          constructionSiteId={
            constructionSiteId ? +constructionSiteId : undefined
          }
          taxCodes={taxCodes}
        />
      )}
    </>
  );
};

export default WetBreyneOverview;
